/* eslint-disable */
import React, { useEffect } from 'react';
import Status from '@ingka/status';
import { navigate, withPrefix } from 'gatsby';
import styled from 'styled-components';
import variables from '@ingka/variables/design-tokens';
import {
  translatePage,
  useTranslation,
} from '@ikea-customer-self-serve/gatsby-plugin-translations';
import { useCart } from '../contexts/Cart';
import { Divider } from '../styles';
import { Item } from '../components/Item';
import SiteMetadata from '../components/SiteMetadata';
import { StatusType } from '../interfaces/Cart';
import CustomerSummary from '../components/CustomerSummary';
import TMarkUp from '../components/TMarkUp';
import { getURLParameters } from '../helpers/url';
import Breadcrumbs from '../components/Breadcrumbs';
import {
  StyledInformation,
  DetailsSection,
  SubTotals,
  Total,
  FlexContainer,
  SummaryLeftCol,
  SummaryRightCol,
  Wrapper,
  SummaryDivider,
  DeliveryStatusText,
  StyledStatus,
  DeliveryStatus,
} from '../styles/status';

const ucFirst = (s: string): string => {
  const lowerCase = s.toLowerCase();
  return lowerCase.charAt(0).toUpperCase() + lowerCase.substr(1);
};

interface StatusProps {
  status: StatusType;
}

const StatusColor: React.FC<StatusProps> = ({ status }): JSX.Element => {
  const statusColor = status === 'CANCELLED' ? 'orange' : 'green';
  const { t } = useTranslation();
  return (
    <StyledStatus
      statusColor={statusColor}
      label={ucFirst(t(`spareParts.summary.status.${status}`))}
      statusDotPosition="trailing"
    />
  );
};

const Summary = (): JSX.Element | null => {
  const { t } = useTranslation();
  const crumbs = [
    { url: withPrefix('/'), text: t('seoContent.page.search') },
    { url: '/summary', text: t('seoContent.page.summary') },
  ];
  const title = t('seoContent.page.summary');
  const cartContext = useCart();

  const items = cartContext.getItems();

  useEffect(() => {
    const order = getURLParameters('order') as string;
    const token = getURLParameters('token') as string;

    if (order && token) {
      navigate(`/status/?order=${order}&token=${token}`);
    } else if (items.length === 0) {
      navigate('/');
    }
  }, [items]);

  if (!items.length) {
    return null;
  }

  const {
    firstName,
    lastName,
    streetAddress,
    streetAddress2,
    streetAddress3,
    postCode,
    city,
    email,
    phoneNumber,
    state,
  } = cartContext.state.recipient;

  const nbrOfItemsText =
    items.length === 1
      ? t('spareParts.summary.items.singular')
      : t('spareParts.summary.items.plural');

  return (
    <>
      <SiteMetadata pageTitle={title} />
      <Breadcrumbs crumbs={crumbs} />
      <FlexContainer>
        <SummaryLeftCol>
          <Wrapper>
            <h1 className="h1">
              <TMarkUp value={t('spareParts.summary.title.highlighted')} />
              <br />
              <TMarkUp value={t('spareParts.summary.title.normal')} />
            </h1>
            <StyledInformation>
              <p>
                <TMarkUp
                  value={t('spareParts.summary.recipient.email', { email })}
                />
              </p>
              <p>
                <TMarkUp
                  value={t('spareParts.summary.shipping.deliveryTime')}
                />
              </p>
            </StyledInformation>
          </Wrapper>
        </SummaryLeftCol>
        <SummaryDivider />
        <SummaryRightCol>
          <DetailsSection>
            <DeliveryStatus>
              <DeliveryStatusText>
                <TMarkUp
                  value={`${t('spareParts.summary.delivery.status')}:`}
                />
                <StatusColor status={'PENDING'} />
              </DeliveryStatusText>
            </DeliveryStatus>
            <CustomerSummary
              firstName={firstName}
              lastName={lastName}
              addressFieldInfo={[streetAddress, streetAddress2, streetAddress3]}
              postCode={postCode}
              city={city}
              email={email}
              phoneNumber={phoneNumber}
              state={state}
            />
          </DetailsSection>
          <DetailsSection>
            <h3 className="h4">{ucFirst(nbrOfItemsText)}</h3>
            <ul>
              {items.map((sparePartItem) => {
                const { item, quantity } = sparePartItem;
                return (
                  <Item item={item} key={item.id}>
                    <TMarkUp
                      value={t('spareParts.summary.items.quantity', {
                        quantity,
                      })}
                    />
                  </Item>
                );
              })}
            </ul>
          </DetailsSection>
          <Divider />
          <SubTotals>
            <span>
              <TMarkUp value={t('spareParts.summary.shipping.subtotal')} />
            </span>
            <span>
              <TMarkUp value={t('spareParts.summary.shipping.free')} />
            </span>
          </SubTotals>
          <SubTotals>
            <span>
              <TMarkUp value={t('spareParts.summary.shipping.shippingCost')} />
            </span>
            <span>
              <TMarkUp value={t('spareParts.summary.shipping.free')} />
            </span>
          </SubTotals>
          <Divider thick />
          <Total>
            <span>
              <TMarkUp value={t('spareParts.summary.shipping.total')} />
            </span>
            <span>
              <TMarkUp value={t('spareParts.summary.shipping.free')} />
            </span>
          </Total>
        </SummaryRightCol>
      </FlexContainer>
    </>
  );
};

export default translatePage()(Summary);
